import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { validationAPI, checkoutAdminAPI } from '../services/api';

interface DailyDetail {
  giorno: number;
  ore_presenza: number;
  ore_ferie: number;
  ore_permesso: number;
  altre_assenze: number;
  motivazione_assenza?: string;
  ore_attese: number;
  ore_totali: number;
  ha_anomalia: boolean;
  differenza_ore: number;
  presence_id?: number;
  check_in?: string;
  check_out?: string;
}

interface PreviewData {
  employee_id: number;
  employee_name: string;
  employee_surname: string;
  year: number;
  month: number;
  month_name: string;
  daily_details: DailyDetail[];
  total_presence_hours: number;
  total_expected_hours: number;
  significant_anomalies_count?: number;
  minor_anomalies_count?: number;
}

interface EditState {
  [dayKey: number]: {
    checkOut: string;
    adminNote: string;
    modified: boolean;
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  employeeName: string;
  userId: number;
  year: number;
  month: number;
  onResolved?: () => void;
}

const formatHours = (value: number) => `${value.toFixed(2)} h`;

const QuickAnomalyResolver: React.FC<Props> = ({
  open,
  onClose,
  employeeName,
  userId,
  year,
  month,
  onResolved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [editState, setEditState] = useState<EditState>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open && userId && year && month) {
      loadPreview();
    }
    return () => {
      setPreview(null);
      setEditState({});
      setError(null);
      setSuccess(null);
    };
  }, [open, userId, year, month]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await validationAPI.getValidationPreview(year, month, userId);
      setPreview(data);
      const initialState: EditState = {};
      data.daily_details.forEach((d: DailyDetail) => {
        if (d.ha_anomalia && d.presence_id) {
          initialState[d.giorno] = {
            checkOut: d.check_out || '',
            adminNote: '',
            modified: false,
          };
        }
      });
      setEditState(initialState);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Errore caricamento dati';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const anomalyDays = preview?.daily_details.filter((d) => d.ha_anomalia) || [];

  const handleCheckOutChange = (day: number, value: string) => {
    setEditState((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        checkOut: value,
        modified: true,
      },
    }));
  };

  const handleNoteChange = (day: number, value: string) => {
    setEditState((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        adminNote: value,
        modified: true,
      },
    }));
  };

  const handleResetDay = (day: number, detail: DailyDetail) => {
    setEditState((prev) => ({
      ...prev,
      [day]: {
        checkOut: detail.check_out || '',
        adminNote: '',
        modified: false,
      },
    }));
  };

  const modifiedDays = Object.entries(editState).filter(([, state]) => state.modified);

  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    let successCount = 0;
    let failCount = 0;

    for (const [dayStr, state] of modifiedDays) {
      const day = parseInt(dayStr);
      const detail = anomalyDays.find((d) => d.giorno === day);
      if (!detail?.presence_id) continue;

      try {
        const payload: Record<string, string | null> = {};

        if (state.checkOut && state.checkOut !== detail.check_out) {
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          payload.check_out = `${dateStr}T${state.checkOut}:00`;
        }

        if (state.adminNote) {
          payload.admin_note = state.adminNote;
        }

        if (Object.keys(payload).length > 0) {
          await checkoutAdminAPI.updatePresence(detail.presence_id, payload);
          successCount++;
        }
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      setSuccess(`${successCount} presenze aggiornate con successo${failCount > 0 ? `, ${failCount} errori` : ''}`);
      await loadPreview();
      if (onResolved) onResolved();
    } else if (failCount > 0) {
      setError(`Errore nell'aggiornamento di ${failCount} presenze`);
    }

    setSaving(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              Risoluzione Rapida Anomalie
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {employeeName} - {preview?.month_name} {year}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : error && !preview ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : preview ? (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <Chip
                label={`Anomalie totali: ${anomalyDays.length}`}
                color={anomalyDays.length > 0 ? 'error' : 'success'}
              />
              {(preview.significant_anomalies_count ?? 0) > 0 && (
                <Chip label={`Significative: ${preview.significant_anomalies_count}`} color="error" variant="outlined" />
              )}
              {(preview.minor_anomalies_count ?? 0) > 0 && (
                <Chip label={`Minori: ${preview.minor_anomalies_count}`} color="warning" variant="outlined" />
              )}
              <Chip
                label={`Ore totali: ${formatHours(preview.total_presence_hours)} / ${formatHours(preview.total_expected_hours)}`}
                color="default"
                variant="outlined"
              />
              {modifiedDays.length > 0 && (
                <Chip label={`${modifiedDays.length} modifiche in sospeso`} color="info" />
              )}
            </Box>

            {anomalyDays.length === 0 ? (
              <Alert severity="success" sx={{ my: 2 }}>
                Nessuna anomalia rilevata. Il cartellino e regolare.
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Giorno</TableCell>
                      <TableCell align="center">Check-in</TableCell>
                      <TableCell align="center">Check-out</TableCell>
                      <TableCell align="right">Ore Presenti</TableCell>
                      <TableCell align="right">Ore Attese</TableCell>
                      <TableCell align="right">Differenza</TableCell>
                      <TableCell align="center">Nuovo Check-out</TableCell>
                      <TableCell>Nota</TableCell>
                      <TableCell align="center">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {anomalyDays.map((detail) => {
                      const dayEdit = editState[detail.giorno];
                      const isModified = dayEdit?.modified;

                      return (
                        <TableRow
                          key={detail.giorno}
                          sx={{
                            backgroundColor: isModified
                              ? 'rgba(25, 118, 210, 0.08)'
                              : detail.differenza_ore > 0.25
                              ? 'rgba(244, 67, 54, 0.06)'
                              : 'rgba(255, 152, 0, 0.06)',
                          }}
                        >
                          <TableCell>
                            <Typography fontWeight={600}>
                              {detail.giorno}/{month}/{year}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {detail.check_in || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {detail.check_out || 'Mancante'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatHours(detail.ore_presenza + detail.ore_permesso + detail.ore_ferie + detail.altre_assenze)}
                          </TableCell>
                          <TableCell align="right">
                            {formatHours(detail.ore_attese)}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`-${detail.differenza_ore.toFixed(2)} h`}
                              color={detail.differenza_ore > 0.25 ? 'error' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {detail.presence_id ? (
                              <TextField
                                type="time"
                                size="small"
                                value={dayEdit?.checkOut || ''}
                                onChange={(e) => handleCheckOutChange(detail.giorno, e.target.value)}
                                sx={{ width: 130 }}
                                InputLabelProps={{ shrink: true }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Nessuna presenza
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              placeholder="Nota giustificativa..."
                              value={dayEdit?.adminNote || ''}
                              onChange={(e) => handleNoteChange(detail.giorno, e.target.value)}
                              sx={{ width: 180 }}
                              multiline
                              maxRows={2}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              {isModified && (
                                <Tooltip title="Annulla modifiche">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleResetDay(detail.giorno, detail)}
                                  >
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Chiudi
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveAll}
          disabled={saving || modifiedDays.length === 0}
        >
          Salva {modifiedDays.length > 0 ? `(${modifiedDays.length} modifiche)` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAnomalyResolver;
